import React from 'react';
import { Route, Redirect } from 'react-router-dom';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import getAllUrlParams from '../../shared/utils/getAllUrlParams';

const LoginRoute = ({
  component: Component,
  authed,
  path,
  ...rest
}) => {
  const currentUser = useSelector((state) => state.auth.data.currentUser);

  return (
    <Route
      {...rest}
      render={
        (props) => {
          if (authed === false) {
            return <Component {...props} />;
          }

          // eslint-disable-next-line react/prop-types
          const currentPath = props.location?.pathname;
          const isIdntyPath = currentPath?.includes('idnty');

          // Wait for currentUser to load before making routing decisions
          if (!currentUser || Object.keys(currentUser).length === 0) {
            if (currentPath === '/signup/registration' || isIdntyPath) {
              return <Component {...props} />;
            }
          }

          // Authed but NOT registered, they need the registration form to complete their profile
          // Also redirect if they accessed other signup routes like /easy-join
          if (currentUser.isRegistered === false) {
            if (currentPath === '/signup/registration' || isIdntyPath) {
              return <Component {...props} />;
            }
            return <Redirect to='/signup/registration' />;
          }

          // User is registered, redirect them away from signup pages
          const url = getAllUrlParams(window.location.href, false);
          let domain;
          if (process.env.PHP_APP_URL && process.env.WEBAPP_URL) {
            // Athletes always go to webapp; admins go to webapp if version=2 or targeting dashboard
            domain = (url.version === '2' || currentUser.admin === 0 || !url.rel || url.rel === 'dashboard')
              ? process.env.WEBAPP_URL
              : process.env.PHP_APP_URL;
          } else {
            const isDevelop = process.env.ENVIRONMENT === 'DEVELOP' ? 'dev.' : '';
            const isV2 = url.version === '2' || currentUser.admin === 0 || !url.rel || url.rel === 'dashboard';
            const subdomain = isV2 ? 'app-v3' : 'app';
            domain = `https://${process.env.ENVIRONMENT === 'STAGING' ? 'staging.' : isDevelop}${subdomain}.teambuildr.com`;
          }
          const isAthlete = currentUser.admin === 0;
          let targetPath = url.rel;
          if (targetPath === undefined) {
            targetPath = isAthlete ? 'workouts' : 'dashboard';
          }

          // Preserve other query parameters (excluding rel and version)
          // getAllUrlParams lowercases param names
          const preservedParams = new URLSearchParams();
          Object.keys(url).forEach((key) => {
            if (key !== 'rel' && key !== 'version') {
              preservedParams.set(key, url[key]);
            }
          });
          const queryString = preservedParams.toString();
          const locationString = `${domain}/${targetPath}${queryString ? `?${queryString}` : ''}`;

          // Returning null causes the registration page to flash a 404 error
          // Returning a spnner instead while the user gets logged in
          if (currentPath === '/signup/registration') {
            window.location = locationString;
            return <Component {...props} />;
          }

          window.location = locationString;
          return null;
        }
      }
    />
  );
};

LoginRoute.propTypes = {
  authed: PropTypes.bool.isRequired,
  component: PropTypes.elementType.isRequired,
  path: PropTypes.string.isRequired,
};

export default LoginRoute;
